import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { CustomRequestConfig as JwtRequestConfig } from '@mathflat/shared/@modules/auth/axiosJwtSetup'
import type { DefaultResponse } from '@mathflat/shared/@types/mathflatApi'
import axios, { type AxiosInstance, type AxiosResponse, HttpStatusCode } from 'axios'
import qs from 'qs'

import ServerMaintenanceService from '~/@pages/serverMaintenance/@service/ServerMaintenance.service'

import { TOAST_STRING } from '../constants/strings'
import { errorHandlerService, toastService } from '../services'
import { envUtils } from './envUtils'
import { reporter } from './errorReporting/reporter'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

/**
 * 404·501 이상은 reject 되지 않고 AxiosError 가 그대로 resolve 된다.
 * 그 경우 응답 봉투가 없어 `data` 가 undefined 이므로, 반환 타입에서 그 가능성을 감춰서는 안 된다.
 */
export type MaybeResponse<T> = Partial<DefaultResponse<T>>

const PREVENT_TOAST_ERROR_CODES = [
  'STUDENT_WORKSHEET_NOT_FOUND',
  'WORKSHEET_NOT_FOUND',
  'WORKSHEET_ALREADY_DELETED',
  'WORKSHEET_PERMISSION_DENIED',
  'WORKSHEET_PROBLEM_EMPTY',
  'SELF_LEARNING_NOT_COMPLETE',
  'STUDENT_WORKSHEET_NOT_COMPLETE',
  'STUDENT_WORKSHEET_PROBLEM_DUPLICATE_SUBMIT',
  'STUDENT_WORKBOOK_PROBLEM_DUPLICATE_SUBMIT',
  'STUDENT_EXAM_PROBLEM_DUPLICATE_SUBMIT',
  'EXAM_NOT_STARTED',
  'EXAM_START_CLOSED',
  'EXAM_NOT_FOUND',
  'EXAM_NOT_VALID',
  'SELF_LEARNING_ALREADY_EXHAUSTED',
  'SELF_LEARNING_ALREADY_CREATED',
  'USER_WRONG_PASSWORD',
  'DAILY_ASSIGNMENT_TIME_EXCEEDED',
  'CREVERSE_SSO_TOKEN_INVALID',
  'VINE_ONE_TIME_TOKEN_INVALID',
]

const BASE_URL = (() => {
  if (envUtils.isStaging) {
    return `https://api-staging.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isLive) {
    return `https://api.${domainProvider.TARGET_DOMAIN}`
  }
  return localStorage.getItem('DEV_API') ?? `https://api-dev.${domainProvider.TARGET_DOMAIN}`
})()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomRequestConfig<D = any> = JwtRequestConfig<D> & {
  /**
   * 이 요청의 실패는 호출부가 모두 직접 안내한다 — 공통 오류 토스트가 같은 자리(toastId)를 먼저 차지하지 않게 막는다.
   * 오프라인 안내는 예외로 그대로 띄운다.
   */
  preventErrorToast?: boolean
  /** 400·401 중 이 코드만 공통 오류 토스트를 막는다 — 호출부가 그 코드만 화면에서 직접 안내할 때 쓴다 */
  preventToastCodes?: string[]
}

/**
 * AxiosInstance를 확장하여 response.data를 바로 반환하는 타입
 */
export interface MaxiosInstance extends AxiosInstance {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request<T = any, R = AxiosResponse<T>['data'], D = any>(
    config: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  head<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  putForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patchForm<T = any, R = AxiosResponse<T>['data'], D = any>(
    url: string,
    data?: D,
    config?: CustomRequestConfig<D>,
  ): Promise<R>
}

const maxios: MaxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    common: {
      'x-platform': 'STUDENT',
      'x-freewheelin-host': envUtils.isSchool ? 'schoolflat.com' : domainProvider.TARGET_DOMAIN,
    },
    post: { 'Content-Type': 'application/json' },
  },
  paramsSerializer: {
    serialize: (params) => {
      return qs.stringify(params, { arrayFormat: 'comma' })
    },
  },
})

maxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

maxios.interceptors.response.use(
  (response) => response.data,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      // 던지는 객체를 그대로 파이프라인에 넘긴다. 취소·4xx 등 버릴지는 파이프라인만 정한다(D-11).
      reporter.capture(error, { mechanism: 'interceptor' })

      // 인터넷 연결을 보장하지 못함 (https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine)
      // navigator.onLine이 undefined일 수 있어서 false와 비교
      if (navigator.onLine === false) {
        toastService.error(TOAST_STRING.networkOffline)
        return Promise.reject(error)
      }

      const axiosError = error as {
        config?: CustomRequestConfig
        response?: { status?: number; data?: { code?: string } }
      }
      const status = axiosError?.response?.status
      const errorCode = axiosError?.response?.data?.code
      const preventErrorToast = axiosError.config?.preventErrorToast === true

      if (!status) {
        errorHandlerService.handle(error as Error, { preventToast: preventErrorToast })
        return Promise.reject(error)
      }

      if (status === HttpStatusCode.NotFound) {
        return error
      }
      if (status >= 501) {
        await ServerMaintenanceService.checkServerMaintenance()
        return error
      }

      errorHandlerService.handle(error as Error, {
        logType: 'error',
        useRemoteLogging: false,
        preventToast:
          preventErrorToast ||
          ((status === HttpStatusCode.BadRequest || status === HttpStatusCode.Unauthorized) &&
            !!errorCode &&
            (PREVENT_TOAST_ERROR_CODES.includes(errorCode) ||
              !!axiosError.config?.preventToastCodes?.includes(errorCode))),
      })

      return Promise.reject(error)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return maxios(originalRequest)
    },
  ),
)

export default maxios
